import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link"; // icon for map template
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // icon for already mapped
import { GridActionsCellItem } from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import PropTypes from "prop-types";

export default function CoursesDialog({ open, onClose, row, user, showSnackbar }) {
  const [courses, setCourses] = useState([]);
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);
  const [students, setStudents] = useState([]); // each student.student_id is a STRING here
  const [selectedStudents, setSelectedStudents] = useState([]); // array of STRING ids
  const [currentCategory, setCurrentCategory] = useState(null);
  const [courseIdsByCategory, setCourseIdsByCategory] = useState({ O: [], L: [], E: [] });
  const [processingStudentId, setProcessingStudentId] = useState(null); // optional to block double clicks
  const [oCategoryAllotted, setOCategoryAllotted] = useState(false);
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [pageSize, setPageSize] = useState(10);

  const [courseSelectDialogOpen, setCourseSelectDialogOpen] = useState(false);
  const [availableCategoryCourses, setAvailableCategoryCourses] = useState([]);
  const [selectedCategoryCourse, setSelectedCategoryCourse] = useState(null);

  const [templateSelectDialog, setTemplateSelectDialog] = useState({
    open: false,
    mode: null, // "single" or "bulk"
    selectedCourse: null,
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [form, setForm] = useState({
    college_id: "",
    program_id: "",
    branch_id: "",
    regulation_id: "",
    year_number: "",
    semester_number: "",
    course_order: 0,
    course_code: "",
    short_code: "",
    course_level: "",
    course_name: "",
    course_type: "",
    course_category: "",
    elective_number: "",
    credits: 0,
    total_minimum: 0,
    internal_maximum: 0,
    internal_minimum: 0,
    external_minimum: 0,
    external_maximum: 0,
    course_part: "",
    course_group: "",
    status: "ACTIVE",
    workflow_status: "DRAFT",
    program: null,
    branch: null,
    regulation: null,
  });
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [colleges, setColleges] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [regulations, setRegulations] = useState([]);
  const [examTemplates, setExamTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [bulkCourseSelection, setBulkCourseSelection] = useState([]);
  let clientId = user.client_id;
  let collegeId = user.college_id;

  useEffect(() => {
    const fetchExamTemplates = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/exam-template/${clientId}/${collegeId}/listapproved`
        );
        setExamTemplates(data.data || []);
      } catch (err) {
        console.error("Failed to fetch exam templates", err);
        showSnackbar("❌ Failed to load exam templates", "error");
      }
    };

    if (open) fetchExamTemplates();
  }, [open]);
  useEffect(() => {
    if (selectedCourse) {
      setForm({
        exam_course_id: selectedCourse.exam_course_id || "",
        course_code: selectedCourse.course_code || "",
        course_order: selectedCourse.course_order || "",
        course_name: selectedCourse.course_name || "",
        course_category: selectedCourse.course_category || "",
        credits: selectedCourse.credits || "",
        internal_minimum: selectedCourse.internal_minimum || "",
        internal_maximum: selectedCourse.internal_maximum || "",
        external_minimum: selectedCourse.external_minimum || "",
        external_maximum: selectedCourse.external_maximum || "",
        total_minimum: selectedCourse.total_minimum || "",

        // ✅ Nested objects
        program: selectedCourse.program || null,
        branch: selectedCourse.branch || null,
        regulation: selectedCourse.regulation || null,

        // ✅ IDs
        program_id: selectedCourse.program_id || "",
        branch_id: selectedCourse.branch_id || "",
        regulation_id: selectedCourse.regulation_id || "",

        year_number: selectedCourse.year_number || "",
        semester_number: selectedCourse.semester_number || "",
        college_id: selectedCourse.college_id || user.college_id,
      });
    } else {
      // New course: pre-fill with college_id
      setForm((f) => ({
        ...f,
        college_id: user.college_id,
      }));
    }
  }, [selectedCourse, user]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };
  const handleCategoryCourseSelect = async () => {
    try {
      if (!selectedCategoryCourse) return;

      setCourseSelectDialogOpen(false);

      // Fetch student list
      const studentsRes = await axios.get(
        `${API_BASE_URL}/api/students/${user.client_id}/${user.college_id}/${row.admittedbatch_id}/${row.branch_id}`
      );

      const studentsListRaw = studentsRes.data?.data || [];
      const normalizedStudents = studentsListRaw.map((s) => ({
        ...s,
        student_id: String(s.student_id),
      }));

      setStudents(normalizedStudents);

      // Check existing allotments for this course
      const allotRes = await axios.post(`${API_BASE_URL}/api/exam-courses/allotments/check`, {
        client_id: user.client_id,
        student_ids: studentsListRaw.map((s) => s.student_id),
        exam_course_ids: [Number(selectedCategoryCourse)],
      });

      const alreadyAllottedSet = new Set(
        (allotRes.data?.data || []).map((item) => String(item.student_id))
      );

      // Preselect already allotted students
      setSelectedStudents(
        normalizedStudents
          .filter((s) => alreadyAllottedSet.has(s.student_id))
          .map((s) => s.student_id)
      );

      setStudentDialogOpen(true);
    } catch (err) {
      console.error("handleCategoryCourseSelect error:", err);
      showSnackbar("❌ Failed to load students", "error");
    }
  };

  const handleSave = async () => {
    try {
      if (selectedCourse?.exam_course_id) {
        await axios.put(
          `${API_BASE_URL}/api/exam-courses/${user.client_id}/${user.college_id}/${selectedCourse.exam_course_id}`,
          {
            ...form,
            modified_by: user.user_email,
          }
        );
        showSnackbar("✅ Exam Course updated successfully", "success");
      } else {
        await axios.post(`/api/exam-courses`, {
          ...form,
          created_by: user.user_email,
        });
      }
      //refreshCourses();
      setCourseDialogOpen(false);
      onClose();
    } catch (err) {
      console.error("Save failed", err);
    }
  };
  const fetchCourses = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/exam-courses`, {
        params: {
          client_id: user.client_id,
          college_id: user.college_id,
          program_id: row.program_id,
          branch_id: row.branch_id,
          regulation_id: row.regulation_id,
          year_number: row.year_number,
          semester_number: row.semester_number,
        },
        headers: { "Cache-Control": "no-cache" },
      });

      const coursesList = data.data || [];
      setCourses(coursesList);

      setCourseIdsByCategory({
        O: coursesList.filter((c) => c.course_category === "O").map((c) => c.exam_course_id),
        S: coursesList.filter((c) => c.course_category === "S").map((c) => c.exam_course_id),
        E: coursesList.filter((c) => c.course_category === "E").map((c) => c.exam_course_id),
      });

      showSnackbar("Courses loaded successfully ✅", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("❌ Failed to load courses", "error");
    }
  };

  useEffect(() => {
    if (open && row) {
      fetchCourses();
    }
  }, [open, row]);

  // Fetch students & pre-check already allotted students for the category
  const fetchStudents = async (category) => {
    try {
      setCurrentCategory(category);

      // 🔹 Special handling for Languages (course_category = "S")
      if (category === "S") {
        // fetch students with second_language column
        const studentsRes = await axios.get(
          `${API_BASE_URL}/api/students/${user.client_id}/${user.college_id}/${row.admittedbatch_id}/${row.branch_id}`
        );

        const studentsListRaw = studentsRes.data?.data || [];
        const normalizedStudents = studentsListRaw.map((s) => ({
          ...s,
          student_id: String(s.student_id),
        }));

        const studentsWithLang = normalizedStudents.filter(
          (s) => s.second_language && s.second_language.trim() !== ""
        );

        // Build map of languageName → exam_course_id
        const langCourseMap = {};
        courses
          .filter((c) => c.course_category === "S")
          .forEach((c) => {
            langCourseMap[c.course_name.trim().toUpperCase()] = c.exam_course_id;
          });

        const allotPayload = [];
        studentsWithLang.forEach((student) => {
          const langKey = student.second_language?.trim().toUpperCase();
          if (langKey && langCourseMap[langKey]) {
            allotPayload.push({
              student_id: Number(student.student_id),
              exam_course_id: Number(langCourseMap[langKey]),
            });
          }
        });

        if (allotPayload.length) {
          for (const { student_id, exam_course_id } of allotPayload) {
            await axios.post(`${API_BASE_URL}/api/exam-courses/allotments/save`, {
              client_id: user.client_id,
              college_id: user.college_id,
              student_ids: [student_id],
              exam_course_ids: [exam_course_id],
              created_by: user.user_email,
            });
          }

          showSnackbar("✅ Languages are allotted", "success");
        } else {
          showSnackbar("⚠️ No matching language courses found", "warning");
        }

        // Update UI state
        setStudents(normalizedStudents);
        setSelectedStudents(allotPayload.map((a) => String(a.student_id)));

        setStudentDialogOpen(true);
        return; // stop here, don’t run default flow
      }

      // 🔹 For Elective (E), show dropdown before loading students
      if (category === "E") {
        const electiveCourses = courses
          .filter(
            (c) =>
              c.course_category === "E" &&
              Number(c.branch_id) === Number(row.branch_id) &&
              Number(c.year_number) === Number(row.year_number) &&
              Number(c.semester_number) === Number(row.semester_number)
          )
          .sort((a, b) => a.course_name.localeCompare(b.course_name)); // ✅ sort alphabetically by name

        if (!electiveCourses.length) {
          showSnackbar("⚠️ No elective courses found", "warning");
          return;
        }
        setAvailableCategoryCourses(electiveCourses);
        setSelectedCategoryCourse(null);
        setCourseSelectDialogOpen(true);
        return; // Wait for user to choose a course
      }

      // 🔹 Default flow for “O” (Open/Other)
      const studentsRes = await axios.get(
        `${API_BASE_URL}/api/students/${user.client_id}/${user.college_id}/${row.admittedbatch_id}/${row.branch_id}`
      );

      const studentsListRaw = studentsRes.data?.data || [];
      const normalizedStudents = studentsListRaw.map((s) => ({
        ...s,
        student_id: String(s.student_id),
      }));

      const courseIds = courseIdsByCategory[category] || [];

      const allotRes = await axios.post(`${API_BASE_URL}/api/exam-courses/allotments/check`, {
        client_id: user.client_id,
        student_ids: studentsListRaw.map((s) => s.student_id),
        exam_course_ids: courseIds,
      });

      const alreadyAllottedSet = new Set(
        (allotRes.data?.data || []).map((item) => String(item.student_id))
      );

      setStudents(normalizedStudents);
      setSelectedStudents(
        normalizedStudents
          .filter((s) => alreadyAllottedSet.has(s.student_id))
          .map((s) => s.student_id)
      );
      setStudentDialogOpen(true);
    } catch (err) {
      console.error("fetchStudents error:", err);
      showSnackbar("❌ Failed to load students", "error");
    }
  };

  // Toggle a single student (checked: boolean) — performs save or delete for that student and currentCategory courseIds
  const handleStudentToggle = async (studentId, checked) => {
    // studentId here is string (normalized)
    // if (!currentCategory) return showSnackbar("Category unknown", "error");

    // const courseIds = courseIdsByCategory[currentCategory] || [];

    if (!selectedCategoryCourse) return showSnackbar("Select a course first", "warning");

    const courseIds = [selectedCategoryCourse];
    if (!courseIds.length) return showSnackbar("No courses for this category", "warning");

    // prevent double clicks on same student
    if (processingStudentId === studentId) return;
    setProcessingStudentId(studentId);

    try {
      if (checked) {
        // save allotments for this student for these courseIds
        await axios.post(`${API_BASE_URL}/api/exam-courses/allotments/save`, {
          client_id: user.client_id,
          college_id: user.college_id,
          student_ids: [Number(studentId)], // convert to number for backend
          exam_course_ids: courseIds.map((id) => Number(id)),
          created_by: user.user_email,
        });

        setSelectedStudents((prev) => {
          if (prev.includes(studentId)) return prev;
          return [...prev, studentId];
        });
        if (currentCategory === "O") {
          setOCategoryAllotted(true);
        }
        const student = students.find((s) => s.student_id === studentId);
        showSnackbar(`✅ Course Allotment saved for ${student?.student_rollno}`, "success");
      } else {
        // delete allotments for this student for these courseIds
        await axios.post(`${API_BASE_URL}/api/exam-courses/allotments/delete`, {
          client_id: user.client_id,
          college_id: user.college_id,
          student_ids: [Number(studentId)],
          exam_course_ids: courseIds.map((id) => Number(id)),
        });
        const student = students.find((s) => s.student_id === studentId);
        setSelectedStudents((prev) => prev.filter((id) => id !== studentId));
        showSnackbar(`❌ Course Allotment removed for ${student?.student_rollno}`, "info");
      }
    } catch (err) {
      console.error("handleStudentToggle error:", err);
      showSnackbar("❌ Operation failed", "error");
    } finally {
      setProcessingStudentId(null);
    }
  };

  // Select/unselect all students in current list for the category
  const handleSelectAll = async (e) => {
    if (!currentCategory) return showSnackbar("Category unknown", "error");

    // Prefer selected course if available
    const courseIds = selectedCategoryCourse
      ? [selectedCategoryCourse]
      : courseIdsByCategory[currentCategory] || [];

    if (!courseIds.length) return showSnackbar("No courses for this category", "warning");

    const allIds = students.map((s) => String(s.student_id));

    try {
      if (e.target.checked) {
        await axios.post(`${API_BASE_URL}/api/exam-courses/allotments/save`, {
          client_id: user.client_id,
          college_id: user.college_id,
          student_ids: allIds.map((id) => Number(id)),
          exam_course_ids: courseIds.map((id) => Number(id)),
          created_by: user.user_email,
        });
        setSelectedStudents(allIds);
        showSnackbar("✅ All students allotted", "success");
      } else {
        /* await axios.post(`${API_BASE_URL}/api/exam-courses/allotments/delete`, {
          client_id: user.client_id,
          college_id: user.college_id,
          student_ids: allIds.map((id) => Number(id)),
          exam_course_ids: courseIds.map((id) => Number(id)),
        }); */
        setSelectedStudents([]);
        showSnackbar("❌ All allotments removed", "info");
      }
    } catch (err) {
      console.error("handleSelectAll error:", err);
      showSnackbar("❌ Operation failed", "error");
    }
  };

  // Confirm just closes the dialog — actual saves/deletes happen per-toggle (as requested)
  const handleConfirm = () => {
    if (oCategoryAllotted) {
      showSnackbar("📢 Open category courses allotted to all students", "info");
      setOCategoryAllotted(false); // reset flag
    }
    setStudentDialogOpen(false);
  };
  const handleMapTemplate = (course) => {
    setTemplateSelectDialog({ open: true, mode: "single", selectedCourse: course });
  };

  // Course grid columns
  const courseColumns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 70,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "course_order", headerName: "CourseOrder", width: 150 },
    { field: "print_order", headerName: "PrintOrder", width: 150 },
    { field: "mid_order", headerName: "MidOrder", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 180,
      getActions: (params) => {
        const isMapped = params.row.map_templates && params.row.map_templates.length > 0;
        const mappedTemplateId = isMapped ? params.row.map_templates[0].exam_template_id : null;

        return [
          <GridActionsCellItem
            key={`edit-${params.id}`}
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleEdit(params.row)}
          />,
          <GridActionsCellItem
            key={`delete-${params.id}`}
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDelete(params.row.exam_course_id)}
          />,
          <GridActionsCellItem
            key={`map-${params.id}`}
            icon={isMapped ? <CheckCircleIcon color="success" /> : <LinkIcon />}
            label={isMapped ? "Mapped" : "Map Template"}
            onClick={() => handleMapTemplate(params.row)}
            showInMenu={false}
          />,
        ];
      },
    },
    { field: "course_code", headerName: "Code", width: 170 },
    { field: "course_name", headerName: "Course Name", width: 270 },
    { field: "course_category", headerName: "Category", width: 100 },
    { field: "credits", headerName: "Credits", width: 150 },
    { field: "internal_minimum", headerName: "Internal Minimum", width: 150 },
    { field: "internal_maximum", headerName: "Internal Maximum", width: 150 },
    { field: "external_minimum", headerName: "External Minimum", width: 150 },
    { field: "external_maximum", headerName: "External Maximum", width: 150 },
    { field: "total_minimum", headerName: "Total Minimum", width: 150 },
  ];
  // Add new
  const handleAdd = () => {
    setSelectedCourse(null);
    setCourseDialogOpen(true);
  };

  // Edit existing
  const handleEdit = (course) => {
    setSelectedCourse({
      ...course,
      program: course.program || null,
      branch: course.branch || null,
      regulation: course.regulation || null,
    });
    setCourseDialogOpen(true);
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/exam-courses/${id}`);
      setCourses((prev) => prev.filter((c) => c.exam_course_id !== id));
      showSnackbar("✅ Course deleted", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("❌ Failed to delete course", "error");
    }
  };
  const handleBulkMap = () => {
    if (!courses.length) return showSnackbar("No courses available", "warning");
    setTemplateSelectDialog({ open: true, mode: "bulk", selectedCourse: null });
  };
  const handleTemplateMappingConfirm = async () => {
    if (!selectedTemplateId) return showSnackbar("⚠️ Please select a template", "warning");

    const { mode, selectedCourse } = templateSelectDialog;

    try {
      if (mode === "single" && selectedCourse) {
        await axios.post(`${API_BASE_URL}/api/exam-template/map`, {
          client_id: user.client_id,
          college_id: user.college_id,
          exam_course_id: selectedCourse.exam_course_id,
          exam_template_id: Number(selectedTemplateId),
          created_by: user.user_email,
        });
        showSnackbar(`✅ Template mapped to ${selectedCourse.course_name}`, "success");
      } else if (mode === "bulk") {
        const requests = courses.map((c) =>
          axios.post(`${API_BASE_URL}/api/exam-template/map`, {
            client_id: user.client_id,
            college_id: user.college_id,
            exam_course_id: c.exam_course_id,
            exam_template_id: Number(selectedTemplateId),
            created_by: user.user_email,
          })
        );
        await Promise.all(requests);
        showSnackbar("✅ All selected courses mapped successfully", "success");
      }

      setTemplateSelectDialog({ open: false, mode: null, selectedCourse: null });
      setSelectedTemplateId("");
      await fetchCourses();
    } catch (err) {
      console.error(err);
      showSnackbar("❌ Template mapping failed", "error");
    }
  };
  const toRoman = (num) => {
    const map = {
      1: "I",
      2: "II",
      3: "III",
      4: "IV",
      5: "V",
      6: "VI",
      7: "VII",
      8: "VIII",
    };
    return map[num] || num;
  };
  return (
    <>
      {/* Courses Dialog */}
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <Typography variant="h6">
          Courses for {toRoman(row?.year_number)} Year - {toRoman(row?.semester_number)} Semester{" "}
          {row?.branch?.branch_name} Branch
        </Typography>
        <DialogContent>
          <Box
            sx={{
              height: 700,
              width: "100%",
              overflowX: "scroll", // enables horizontal scroll
            }}
          >
            <DataGrid
              rows={courses}
              columns={courseColumns}
              getRowId={(r) => r.exam_course_id}
              pageSize={pageSize} // controlled pageSize
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // update state
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              pagination
              disableSelectionOnClick
              disableColumnMenu
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Grid container alignItems="center" sx={{ gap: 1, flexWrap: "wrap" }}>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={() => fetchStudents("O")}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#115293" },
                }}
              >
                Open(All)
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={() => fetchStudents("S")}
                disabled={!courseIdsByCategory.S || courseIdsByCategory.S.length === 0}
                sx={{
                  backgroundColor:
                    !courseIdsByCategory.S || courseIdsByCategory.S.length === 0
                      ? "#999"
                      : "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor:
                      !courseIdsByCategory.S || courseIdsByCategory.S.length === 0
                        ? "#999"
                        : "#115293",
                  },
                }}
              >
                Languages
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={() => fetchStudents("E")}
                disabled={!courseIdsByCategory.E || courseIdsByCategory.E.length === 0}
                sx={{
                  backgroundColor:
                    !courseIdsByCategory.E || courseIdsByCategory.E.length === 0
                      ? "#999"
                      : "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor:
                      !courseIdsByCategory.E || courseIdsByCategory.E.length === 0
                        ? "#999"
                        : "#115293",
                  },
                }}
              >
                Electives
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={handleBulkMap}
                sx={{ backgroundColor: "#1976d2", color: "#fff" }}
              >
                Bulk Map Templates
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                onClick={onClose}
                sx={{
                  backgroundColor: "rgb(238, 30, 30)",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "rgba(251, 41, 41, 0.92)", color: "#fff" },
                }}
              >
                Close
              </Button>
            </Grid>{" "}
          </Grid>
        </DialogActions>
      </Dialog>

      {/* Student Selection Dialog */}
      <Dialog
        open={studentDialogOpen}
        onClose={() => setStudentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Select Students (
          {currentCategory === "O" ? "Open" : currentCategory === "S" ? "Language" : "Elective"}{" "}
          Courses )
        </DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                onChange={handleSelectAll}
                checked={selectedStudents.length === students.length && students.length > 0}
                indeterminate={
                  selectedStudents.length > 0 && selectedStudents.length < students.length
                }
              />
            }
            label="Select All"
          />

          <Grid container spacing={2}>
            {students.map((student) => (
              <Grid item xs={12} sm={6} md={3} key={student.student_id}>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={processingStudentId === student.student_id}
                        checked={selectedStudents.includes(String(student.student_id))}
                        onChange={(e) =>
                          handleStudentToggle(String(student.student_id), e.target.checked)
                        }
                      />
                    }
                    label={student.student_rollno}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStudentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#115293" },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={courseDialogOpen}
        onClose={() => setCourseDialogOpen(false)} // ✅ use the setter
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedCourse ? "Edit Course" : "Add Course"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Program */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                disabled
                label="Program"
                value={form.program?.program_name || ""}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>

            {/* Branch */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                disabled
                label="Branch"
                value={form.branch?.branch_name || ""}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>

            {/* Regulation */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                disabled
                label="Regulation"
                value={form.regulation?.regulation_code || ""}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>

            {/* Year & Semester */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                disabled
                label="Year"
                value={form.year_number}
                onChange={(e) => handleChange("year_number", e.target.value)}
              ></TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                disabled
                label="Semester"
                value={form.semester_number}
                onChange={(e) => handleChange("semester_number", e.target.value)}
              ></TextField>
            </Grid>

            {/* Course Code */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Course Order"
                value={form.course_order}
                onChange={(e) => handleChange("course_order", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Print Order"
                value={form.print_order}
                onChange={(e) => handleChange("print_order", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Mid Order(if  value 0, then not selected for MID"
                value={form.mid_order}
                onChange={(e) => handleChange("mid_order", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Course Code"
                value={form.course_code}
                onChange={(e) => handleChange("course_code", e.target.value)}
              />
            </Grid>

            {/* Course Name */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Course Name"
                value={form.course_name}
                onChange={(e) => handleChange("course_name", e.target.value)}
              />
            </Grid>

            {/* Course Category */}
            <Grid item xs={6}>
              <TextField
                select
                margin="normal"
                label="Course-Category"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.course_category}
                onChange={(e) => setForm((prev) => ({ ...prev, course_category: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="">Select Course-Category</option>
                <option value="O">Open</option>
                <option value="E">Elective</option>
                <option value="S">SecondLanguage</option>
              </TextField>
            </Grid>

            {/* Credits */}
            <Grid item xs={6}>
              <TextField
                type="number"
                fullWidth
                label="Credits"
                value={form.credits}
                onChange={(e) => handleChange("credits", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type="number"
                fullWidth
                label="internal_minimum"
                value={form.internal_minimum}
                onChange={(e) => handleChange("internal_minimum", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type="number"
                fullWidth
                label="internal_maximum"
                value={form.internal_maximum}
                onChange={(e) => handleChange("internal_maximum", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type="number"
                fullWidth
                label="external_minimum"
                value={form.external_minimum}
                onChange={(e) => handleChange("external_minimum", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type="number"
                fullWidth
                label="external_maximum"
                value={form.external_maximum}
                onChange={(e) => handleChange("external_maximum", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type="number"
                fullWidth
                label="total_minimum"
                value={form.total_minimum}
                onChange={(e) => handleChange("total_minimum", e.target.value)}
              />
            </Grid>
            {/* Course Part */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Course Part"
                value={form.course_part}
                onChange={(e) => handleChange("course_part", e.target.value)}
              />
            </Grid>

            {/* Course Group */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Course Group"
                value={form.course_group}
                onChange={(e) => handleChange("course_group", e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCourseDialogOpen(false)}
            sx={{
              backgroundColor: "rgb(210, 25, 40)",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#b7bd84ff" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: "rgb(25, 47, 210)",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#b7bd84ff" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={templateSelectDialog.open}
        onClose={() => setTemplateSelectDialog({ open: false, mode: null, selectedCourse: null })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {templateSelectDialog.mode === "bulk"
            ? "Bulk Map Templates"
            : `Map Template for Course: ${templateSelectDialog.selectedCourse?.course_name || ""}`}
        </DialogTitle>
        <Typography
          sx={{
            color: "#d32f2f",
            textAlign: "left",
            fontWeight: "bold",
            animation: "blink 1s infinite",
            "@keyframes blink": {
              "0%": { opacity: 1 },
              "50%": { opacity: 0 },
              "100%": { opacity: 1 },
            },
          }}
        >
          ..... Once mapped, the template cannot be edited or deleted.
        </Typography>
        <DialogContent>
          <TextField
            select
            margin="normal"
            label="Select Exam Template"
            fullWidth
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
          >
            <option value="">Select Exam Template</option>
            {examTemplates.map((t) => (
              <option key={t.exam_template_id} value={t.exam_template_id}>
                {t.template_name}
              </option>
            ))}
          </TextField>

          {/* --- TEMPLATE PREVIEW --- */}
          {selectedTemplateId &&
            (() => {
              const selectedTemplate = examTemplates.find(
                (t) => t.exam_template_id === Number(selectedTemplateId)
              );
              if (!selectedTemplate) return null;

              return (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Template Preview
                  </Typography>

                  {/* Template Summary */}
                  <Typography variant="body2">
                    <strong>Name:</strong> {selectedTemplate.template_name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Marks:</strong> {selectedTemplate.exam_marks}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duration:</strong> {selectedTemplate.exam_duration} mins
                  </Typography>
                  <Typography variant="body2">
                    <strong>Created On:</strong>{" "}
                    {new Date(selectedTemplate.created_on).toLocaleString()}
                  </Typography>

                  {/* Sections */}
                  {selectedTemplate.sections && selectedTemplate.sections.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        <strong>Sections:</strong>
                      </Typography>

                      {selectedTemplate.sections.map((section, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 2,
                            p: 1.5,
                            border: "1px dashed #ccc",
                            borderRadius: 1,
                            backgroundColor: "#fff",
                          }}
                        >
                          <Typography variant="subtitle2">
                            {section.section_label || `Section ${index + 1}`} —{" "}
                            {section.section_title}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Questions:</strong> {section.total_questions} |{" "}
                            <strong>Marks/Q:</strong> {section.marks_per_question}
                          </Typography>
                          {section.section_instructions && (
                            <Typography variant="body2" color="text.secondary">
                              {section.section_instructions}
                            </Typography>
                          )}

                          {/* Optional Questions Preview */}
                          {section.questions && section.questions.length > 0 && (
                            <Box sx={{ mt: 1, ml: 2 }}>
                              {section.questions.slice(0, 3).map((q, qi) => (
                                <Typography key={qi} variant="body2" color="text.secondary">
                                  • {q.question_text || `Question ${qi + 1}`}
                                </Typography>
                              ))}
                              {section.questions.length > 3 && (
                                <Typography variant="body2" color="text.disabled">
                                  (+{section.questions.length - 3} more…)
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      No sections found in this template.
                    </Typography>
                  )}
                </Box>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setTemplateSelectDialog({ open: false, mode: null, selectedCourse: null })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleTemplateMappingConfirm}
            sx={{ backgroundColor: "#1976d2", color: "#fff" }}
            disabled={!selectedTemplateId}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Course Select Dialog */}
      <Dialog
        open={courseSelectDialogOpen}
        onClose={() => setCourseSelectDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select {currentCategory === "S" ? "Language" : "Elective"} Course</DialogTitle>
        <DialogContent dividers>
          <TextField
            select
            margin="normal"
            label="Available Courses"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={selectedCategoryCourse || ""}
            onChange={(e) => setSelectedCategoryCourse(e.target.value)}
            SelectProps={{ native: true }}
          >
            {" "}
            <option value="">Select Course</option>{" "}
            {availableCategoryCourses.map((course, idx) => (
              <option key={idx} value={course.exam_course_id}>
                {" "}
                {course.course_name} {"- "}
                {course.course_code}
              </option>
            ))}{" "}
          </TextField>{" "}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCourseSelectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedCategoryCourse}
            onClick={() => handleCategoryCourseSelect()}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#115293" },
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

CoursesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  showSnackbar: PropTypes.func.isRequired,
  user: PropTypes.shape({
    client_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    college_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    user_email: PropTypes.string.isRequired,
  }).isRequired,
  row: PropTypes.shape({
    program_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    branch_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    branch: PropTypes.shape({
      branch_name: PropTypes.string,
    }),
    exam_course_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    regulation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    admittedbatch_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    year_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    semester_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
};
